import React, { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import SearchSelect from '../../components/SearchSelect';
import { loadMaterialOptions, skuBindingXcodes, SkuRow, updateSkuGlobal } from '../../api/material';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import { I18n, i18nText } from '../../utils/i18n';
import { toast } from '../../utils/toast';
import { SkuPropSelect } from '../../components/SkuProp';
import { SkuSpecInput } from '../../components/SkuSpec';
import { specDraftOf, useSkuSchemas, validateSkuSpec } from '../../utils/skuSpec';
import { SKU_STATUS_OPTS } from './selectOptions';

interface Props {
    open: boolean;
    /** 被编辑的 SKU (打开时的快照, 用于预填表单; 含绑定材料清单) */
    sku: SkuRow | null;
    onClose: () => void;
    /** 直接入库成功后回调 (走审批流时数据未变, 不触发) */
    onSaved?: () => void;
}

// SKU 全局编辑: 未绑定材料的 SKU 也能编辑; 绑定材料为多选清单 (多对多, 2026-08-24),
// 提交时全量替换, 首个为主绑定. 字段布局与 SkuCreateModal 对齐 (sku_code 是业务主键, 只读).
function SkuEditModal({ open, sku, onClose, onSaved }: Props) {
    const [nameZh, setNameZh] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [xcodes, setXcodes] = useState<string[]>([]);
    const [remark, setRemark] = useState('');
    const [prop, setProp] = useState('');
    const [status, setStatus] = useState('on_shelf');
    const [cmsId, setCmsId] = useState('');
    const [spec, setSpec] = useState(specDraftOf(null));
    const { byKey: specSchemas } = useSkuSchemas();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !sku) return;
        setNameZh(sku.productName?.zh || '');
        setNameEn(sku.productName?.en || '');
        setXcodes(skuBindingXcodes(sku));
        setRemark(sku.remark || '');
        setProp(sku.prop || '');
        setStatus(sku.status || 'on_shelf');
        setCmsId(sku.cmsId > 0 ? String(sku.cmsId) : '');
        setSpec(specDraftOf(sku));
        setError('');
    }, [open, sku]);

    if (!sku) return null;

    // 材料名查找 (来自打开时的行数据; 本次会话新添加的绑定还没有名字, 只显示编码).
    const bindName = (x: string): string =>
        i18nText(sku.materials?.find((m) => m.xcode === x)?.name || {});

    const onSubmit = async () => {
        if (cmsId.trim() && !/^\d+$/.test(cmsId.trim())) {
            setError('CMS 耗材ID 必须是整数');
            return;
        }
        const [specPayload, specErr] = validateSkuSpec(prop, spec, specSchemas);
        if (specErr) {
            setError(specErr);
            return;
        }
        const productName: I18n = {};
        if (nameZh.trim()) productName.zh = nameZh.trim();
        if (nameEn.trim()) productName.en = nameEn.trim();
        setSubmitting(true);
        setError('');
        try {
            const res = await updateSkuGlobal(sku.skuCode, {
                productName: Object.keys(productName).length > 0 ? productName : undefined,
                prop,
                status,
                cmsId: cmsId.trim() ? Number(cmsId.trim()) : 0,
                remark: remark.trim(),
                ...specPayload,
                xcodes,
            });
            if (alertApprovalIfPending(res.message)) {
                onClose();
                return;
            }
            toast(`SKU ${sku.skuCode} 已更新`);
            onSaved?.();
            onClose();
        } catch (e) {
            setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title={`编辑 SKU · ${sku.skuCode}`} width={680}>
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <label className="field-label">SKU 编号 (业务主键不可改)</label>
                        <input className="inp font-mono bg-slate-50" value={sku.skuCode} disabled />
                    </div>
                    <div>
                        <label className="field-label">产品中文名称</label>
                        <input
                            className="inp"
                            value={nameZh}
                            onChange={(e) => setNameZh(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="field-label">产品英文名称</label>
                        <input
                            className="inp"
                            value={nameEn}
                            onChange={(e) => setNameEn(e.target.value)}
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="field-label">备注 (商品链接)</label>
                        <input
                            className="inp"
                            value={remark}
                            onChange={(e) => setRemark(e.target.value)}
                            placeholder="https://…"
                        />
                    </div>
                    <div>
                        <label className="field-label">属性</label>
                        <SkuPropSelect className="sel w-full" value={prop} onChange={setProp} />
                    </div>
                    <div>
                        <label className="field-label">状态</label>
                        <SearchSelect
                            className="w-full"
                            value={status}
                            onChange={(v) => setStatus(v)}
                            options={SKU_STATUS_OPTS}
                        />
                    </div>
                    <div>
                        <label className="field-label">CMS 耗材ID (清空 = 解除映射)</label>
                        <input
                            className="inp font-mono"
                            value={cmsId}
                            onChange={(e) => setCmsId(e.target.value)}
                            placeholder="仅数字"
                        />
                    </div>
                    <div className="col-span-2">
                        <SkuSpecInput prop={prop} value={spec} onChange={setSpec} />
                    </div>
                    <div className="col-span-2">
                        <label className="field-label">绑定材料 (点 ★ 换主绑定)</label>
                        <div className="space-y-1.5">
                            {xcodes.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {xcodes.map((x, i) => {
                                        const primary = i === 0;
                                        return (
                                            <span
                                                key={x}
                                                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
                                                    primary
                                                        ? 'border-amber-300 bg-amber-50 text-amber-800'
                                                        : 'border-slate-200 bg-slate-50 text-slate-600'
                                                }`}
                                            >
                                                <button
                                                    type="button"
                                                    title={
                                                        primary
                                                            ? '主绑定 (扫码等场景的默认材料)'
                                                            : '设为主绑定'
                                                    }
                                                    className={
                                                        primary
                                                            ? 'text-amber-500 cursor-default'
                                                            : 'text-slate-300 hover:text-amber-500'
                                                    }
                                                    onClick={() => {
                                                        if (!primary) {
                                                            setXcodes([
                                                                x,
                                                                ...xcodes.filter((v) => v !== x),
                                                            ]);
                                                        }
                                                    }}
                                                >
                                                    ★
                                                </button>
                                                <span className="font-mono">{x}</span>
                                                {bindName(x) && (
                                                    <span
                                                        className={`max-w-[7rem] truncate ${
                                                            primary
                                                                ? 'text-amber-600/70'
                                                                : 'text-slate-400'
                                                        }`}
                                                    >
                                                        {bindName(x)}
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    title="移除该绑定"
                                                    className={`ml-0.5 ${
                                                        primary
                                                            ? 'text-amber-400 hover:text-rose-500'
                                                            : 'text-slate-400 hover:text-rose-500'
                                                    }`}
                                                    onClick={() =>
                                                        setXcodes(xcodes.filter((v) => v !== x))
                                                    }
                                                >
                                                    ✕
                                                </button>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                            <SearchSelect
                                loadOptions={loadMaterialOptions}
                                value=""
                                valueLabel=""
                                onChange={(v) => {
                                    if (v && !xcodes.includes(v)) setXcodes([...xcodes, v]);
                                }}
                                placeholder={
                                    xcodes.length === 0
                                        ? '未绑定材料 — 搜索并添加'
                                        : '搜索材质编码 / 材料名, 选中即添加'
                                }
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button type="button" onClick={onClose} className="btn-ghost" disabled={submitting}>
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={onSubmit}
                        disabled={submitting}
                    >
                        {submitting ? '提交中…' : '保存'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default SkuEditModal;
